import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useQuasar } from 'quasar';
import { $t } from 'src/services/i18n';

// Estado global de rede
export const networkState = {
  isOnline: ref(navigator.onLine),
  isChecking: ref(false),
  publicIp: ref(null),
  apiState: ref('online'), // 'online' | 'retrying' | 'offline'
  isInitialized: false,
};

export function setApiState(state) {
  if (['online', 'retrying', 'offline'].includes(state)) {
    networkState.apiState.value = state;
  }
}

// Busca do IP público via serviço externo
export async function fetchPublicIp(timeoutMs = 5000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    });
    clearTimeout(timer);
    const data = await response.json();
    if (data && data.ip) {
      networkState.publicIp.value = data.ip;
      return data.ip;
    }
  } catch (e) {
    console.warn('Não foi possível obter o IP público:', e.message);
  } finally {
    clearTimeout(timer);
  }
  return null;
}

export async function getPublicIp() {
  if (networkState.publicIp.value) return networkState.publicIp.value;
  return await fetchPublicIp();
}

export async function checkOnlineAndNotify() {
  return networkState.isOnline.value;
}

export async function runIfOnline(callback, throwIfOffline = false) {
  const online = navigator.onLine;
  if (online && typeof callback === 'function') {
    return callback();
  }
  if (throwIfOffline) {
    throw new Error($t('network.noConnection'));
  }
  return false;
}

export function getNetworkStatusText() {
  return networkState.isOnline.value ?
    ($t('network.online') || 'Online') :
    ($t('network.offline') || 'Offline');
}

export function getNetworkStatusColor() {
  return networkState.isOnline.value ? 'positive' : 'negative';
}

// Event handlers
const handleOffline = () => {
  networkState.isOnline.value = false;
  setApiState('offline');
};

const handleOnline = () => {
  networkState.isOnline.value = true;
};

function initializeNetworkSystem() {
  if (networkState.isInitialized) return;

  networkState.isInitialized = true;
  networkState.isOnline.value = navigator.onLine;

  fetchPublicIp(); // Busca inicial para log/diagnóstico

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
}

function cleanupNetworkSystem() {
  if (!networkState.isInitialized) return;
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);
  networkState.isInitialized = false;
}

export function useNetworkStatus() {
  if (!networkState.isInitialized && process.env.CLIENT) {
    onMounted(() => initializeNetworkSystem());
    onBeforeUnmount(() => cleanupNetworkSystem());
  }

  return {
    isOnline: networkState.isOnline,
    isChecking: networkState.isChecking,
    publicIp: networkState.publicIp,
    checkOnlineAndNotify,
    getPublicIp,
    runIfOnline,
    getNetworkStatusText,
    getNetworkStatusColor,
    apiState: networkState.apiState,
    setApiState
  };
}
